import { ConfirmModal, useModalContext } from '@fourthwall/components'

import type { OnboardingProps } from '../../../../../../../../components/common/Onboarding'
import { useDashboardFeatureFlags } from '../../../../../../../../components/utils/DashboardFeatureFlags'
import { checklistDataLayer } from '../../../../../../../../utils/gtm/checklistDataLayer'
import { useMembershipsStatusQuery } from '../../../hooks/useMembershipsStatusQuery'
import { getSteps, mergeStep } from '../utils'
import { useHideMembershipsOnboardingMutation } from './useHideMembershipsOnboardingMutation'
import { useMembershipsOnboardingData } from './useMembershipsOnboardingData'
import { useSkipMembershipsOnboardingStepMutation } from './useSkipMembershipsOnboardingStepMutation'

type Props = {
  page: 'Home' | 'MembershipsOverview'
  title?: string
}

export const useMembershipsOnboarding = ({ page, title }: Props) => {
  const modal = useModalContext()
  const { hideMembershipsOnboardingMutation } = useHideMembershipsOnboardingMutation()
  const { skipMembershipsOnboardingStepMutation } = useSkipMembershipsOnboardingStepMutation()
  const { membershipsOnboardingQuery, isOnboardingCompleted, isOnboardingHidden, onboardingSteps } =
    useMembershipsOnboardingData()
  const { membershipsStatusQuery } = useMembershipsStatusQuery()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const steps = getSteps(page)

  const getItems = (): OnboardingProps['onboardingListProps']['items'] => {
    if (!onboardingSteps) {
      return []
    }

    const mergedSteps = steps
      .map((step) => {
        const onboardingStep = onboardingSteps.find(
          (onboardingStep) => onboardingStep.type === step.type,
        )

        if (!onboardingStep) {
          return
        }

        const onSkipStepConfirm = async () => {
          await skipMembershipsOnboardingStepMutation.mutateAsync([onboardingStep.id], {
            onSuccess: membershipsOnboardingQuery.refetch,
          })

          checklistDataLayer('skipChecklistStepConfirm', page, 'Memberships', step.title)
        }

        return mergeStep(step, onboardingStep, async () => {
          modal.open(ConfirmModal, {
            title: `Are you sure you want to skip “${step.title}” step?`,
            text: 'This action cannot be reversed.',
            confirmLabel: 'Skip',
            confirmButtonAnalytics: 'skipChecklistStepConfirm',
            onConfirm: onSkipStepConfirm,
          })

          checklistDataLayer('skipChecklistStep', page, 'Memberships', step.title)
        })
      })
      .filter(Boolean) as OnboardingProps['onboardingListProps']['items']

    return mergedSteps
  }

  const getProgress = () => {
    const items = getItems()
    if (!items.length) {
      return { title: '', percentage: 0 }
    }

    const completedStepsCount = items?.filter((step) => step.completed).length

    const percentage = Math.floor((completedStepsCount * 100) / items.length)

    return {
      title: title ? `${title} (${percentage}% complete)` : `${percentage}% complete`,
      percentage,
    }
  }

  const getActive = () => {
    if (!membershipsOnboardingQuery.isSuccess || !membershipsStatusQuery.isSuccess) {
      return false
    }

    if (isOnboardingCompleted || isOnboardingHidden) {
      return false
    }

    if (page === 'Home' && membershipsStatusQuery.data.status !== 'Live') {
      return false
    }

    return true
  }

  const onSkipConfirm = () => {
    hideMembershipsOnboardingMutation.mutate([], {
      onSuccess: () => {
        membershipsOnboardingQuery.refetch()
      },
    })

    checklistDataLayer('skipChecklistConfirm', 'Home', 'Memberships')
  }

  const onSkip = () => {
    modal.open(ConfirmModal, {
      title: 'Are you sure you want to skip memberships checklist?',
      text: 'This action cannot be reversed.',
      confirmLabel: 'Skip',
      confirmButtonAnalytics: 'skipChecklistConfirm',
      onConfirm: onSkipConfirm,
    })

    checklistDataLayer('skipChecklist', 'Home', 'Memberships')
  }

  return {
    membershipsOnboardingQuery,
    hideMembershipsOnboardingMutation,
    progress: getProgress(),
    items: getItems(),
    active: getActive(),
    hidden: membershipsOnboardingQuery.isSuccess ? isOnboardingHidden : true,
    isLoading: dashboardFeatureFlags.memberships.enabled
      ? membershipsOnboardingQuery.isLoading || membershipsStatusQuery.isLoading
      : false,
    skipLabel: 'Skip all',
    onSkipConfirm,
    onSkip,
  }
}
